import React from 'react';
import { cn, formatCurrency } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertCircle, CreditCard, TrendingUp } from 'lucide-react';
import type { CustomerBalance } from '@/hooks/useCustomerBalance';

interface CustomerBalanceDisplayProps {
  /** Balance data from useCustomerBalance or useCustomerBalances */
  balance: CustomerBalance | null | undefined;
  /** Display variant */
  variant?: 'inline' | 'compact' | 'detailed';
  /** Whether to show the tooltip with full details */
  showTooltip?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * CustomerBalanceDisplay - Shows customer financial summary
 * 
 * Variants:
 * - inline: Single line showing outstanding amount with color coding
 * - compact: Minimal display for table cells
 * - detailed: Full breakdown with credits
 * 
 * @example
 * <CustomerBalanceDisplay 
 *   balance={customerBalance} 
 *   variant="compact" 
 * />
 */
const CustomerBalanceDisplay: React.FC<CustomerBalanceDisplayProps> = ({
  balance,
  variant = 'compact',
  showTooltip = true,
  className
}) => {
  if (!balance) {
    return <span className="text-muted-foreground">—</span>;
  }


  const getOutstandingColor = () => {
    if (balance.hasOverdue) return 'text-destructive';
    if (balance.totalOutstanding > 0) return 'text-amber-600';
    if (balance.totalOutstanding === 0 && balance.totalInvoiced > 0) return 'text-green-600';
    return 'text-muted-foreground';
  };

  const tooltipContent = (
    <div className="space-y-1 text-xs">
      <div className="flex justify-between gap-4">
        <span>Total Invoiced:</span>
        <span className="font-medium">{formatCurrency(balance.totalInvoiced)}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span>Total Paid:</span>
        <span className="font-medium text-green-600">{formatCurrency(balance.totalPaid)}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span>Outstanding:</span>
        <span className={cn("font-medium", getOutstandingColor())}>
          {formatCurrency(balance.totalOutstanding)}
        </span>
      </div>
      {balance.hasOverdue && (
        <div className="flex justify-between gap-4 text-destructive">
          <span>Overdue:</span>
          <span className="font-medium">{formatCurrency(balance.overdueAmount)}</span>
        </div>
      )}
      {balance.totalCredits > 0 && (
        <div className="flex justify-between gap-4 text-blue-600">
          <span>Available Credits:</span>
          <span className="font-medium">{formatCurrency(balance.totalCredits)}</span>
        </div>
      )}
    </div>
  );

  const content = () => {
    switch (variant) {
      case 'inline':
        return (
          <div className={cn("flex items-center gap-1.5", className)}>
            {balance.hasOverdue && (
              <AlertCircle className="h-3 w-3 text-destructive" />
            )}
            <span className={cn("text-sm font-medium", getOutstandingColor())}>
              {formatCurrency(balance.totalOutstanding)}
            </span>
            {balance.totalCredits > 0 && (
              <CreditCard className="h-3 w-3 text-blue-600" />
            )}
          </div>
        );

      case 'compact':
        return (
          <div className={cn("flex flex-col", className)}>
            <span className={cn("text-sm font-medium", getOutstandingColor())}>
              {balance.totalOutstanding === 0 && balance.totalInvoiced === 0 
                ? '—' 
                : formatCurrency(balance.totalOutstanding)}
            </span>
            {balance.hasOverdue && (
              <span className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Overdue
              </span>
            )}
          </div>
        );

      case 'detailed':
        return (
          <div className={cn("space-y-1", className)}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Outstanding:</span>
              <span className={cn("font-medium", getOutstandingColor())}>
                {formatCurrency(balance.totalOutstanding)}
              </span>
            </div>
            {balance.hasOverdue && (
              <div className="flex items-center justify-between text-xs text-destructive">
                <span className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Overdue:
                </span>
                <span className="font-medium">{formatCurrency(balance.overdueAmount)}</span>
              </div>
            )}
            {balance.totalCredits > 0 && (
              <div className="flex items-center justify-between text-xs text-blue-600">
                <span className="flex items-center gap-1">
                  <CreditCard className="h-3 w-3" />
                  Credits:
                </span>
                <span className="font-medium">{formatCurrency(balance.totalCredits)}</span>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!showTooltip) {
    return content();
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <div className="cursor-help">
            {content()}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CustomerBalanceDisplay;
